<template>
  <div
    v-if="discussion"
    class="discussion-component-container"
    :class="{ 'child-discussion': isChildDiscussion }"
  >
    <div
      :class="{ hlt: discussion.hlt }"
      class="details"
    >
      {{ discussionName }}
      <img
        v-if="discussion.hlt && adminIcon"
        :src="adminIcon"
        alt=""
        role="presentation"
      >
      <span
        v-if="label"
        class="label"
      >{{ label }}</span>
    </div>
    <discussion-editor
      v-if="activeInput === editInputKey"
      :active="true"
      :discussion="discussion"
      :editing="true"
      :input-key="editInputKey"
      @cancel="setEditing(false)"
      @edit-discussion="editDiscussion"
      @create-discussion="createDiscussion"
    />
    <div
      v-if="discussion.hlt"
      class="message"
      v-html="sanitizedMessage"
    ></div>
    <div
      v-else
      class="message"
    >
      {{ discussion.message }}
    </div>
    <div class="actions">
      <button
        v-if="canEditDiscussion"
        @click="setInput(editInputKey)"
      >
        Edit
      </button>
      <button
        v-if="showReplyAction"
        @click="setInput(replyInputKey)"
      >
        Reply
      </button>
      <button
        v-if="shouldShowFollowUnfollow"
        @click="followUnfollow"
      >
        {{ followUnfollowText }}
      </button>
      <button
        v-if="canEditDiscussion"
        class="delete"
        @click="triggerDelete(discussion)"
      >
        Delete
      </button>
      <button
        v-if="showExpandAction"
        class="reply-button"
        @click="toggleExpanded"
      >
        {{ expandText }}
      </button>
      <slot name="rating" />
    </div>
    <discussion-editor
      v-if="activeInput === replyInputKey"
      :active="true"
      :input-key="replyInputKey"
      :parent-discussion="discussion"
      @cancel="setInput(null)"
      @create-discussion="createDiscussion"
    />
    <div
      v-if="expandedReplies"
      class="replies"
    >
      <slot name="replies" />
    </div>
    <time
      v-if="showDate && discussion.createdAt"
      class="posted-at"
      :datetime="discussion.createdAt"
    >
      {{ postedAt }}
    </time>
  </div>
</template>

<script>
import DiscussionEditor from '@/components/discussions-v2/DiscussionEditor';
import { format } from 'date-fns';
import { timeAgo } from '@/utils/date';
import { sanitizeHtml } from '@/utils/html';

export default {
  name: 'DiscussionMessage',
  components: { DiscussionEditor },
  props: {
    activeInput: {
      type: String,
    },
    allowReply: {
      type: Boolean,
      default: true,
    },
    childDiscussionCount: {
      type: Number,
      default: 0,
    },
    discussion: {
      type: Object,
    },
    // Small tag shown beside the author name, e.g. 'Original Post'
    label: {
      type: String,
      default: '',
    },
    parentDiscussion: {
      type: Object,
    },
    allowFollow: {
      type: Boolean,
      default: true,
    },
    showDate: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
    },
  },
  emits: ['create-discussion', 'edit-discussion', 'unfollowed-discussion', 'followed-discussion', 'set-input', 'expanded-replies', 'trigger-delete'],
  data() {
    return {
      editing: false,
      expandedReplies:  true,
    };
  },
  computed: {
    adminIcon() {
      const pv = this.$store.getters['productVars/get']['adminDiscussionIcon'];
      return pv?.value;
    },
    canEditDiscussion() {
      return this.discussion.userId === this.user?.id;
    },
    discussionName() {
      let name = this.discussion?.firstName;
      if (this.discussion?.lastName) {
        name = name + ' ' + this.discussion.lastName;
      }
      return name || '';
    },
    editInputKey() {
      return `discussion-edit-${this.discussion.id}`;
    },
    expandText() {
      const expandActionText = this.expandedReplies ? 'Hide' : 'Show';
      const replyText = this.childDiscussionCount === 1 ? 'Reply' : 'Replies';
      return `${expandActionText} ${this.childDiscussionCount} ${replyText}`; // Ex. 'Show 7 replies'
    },
    followUnfollowText() {
      return this.discussion.following ? 'Unfollow' : 'Follow';
    },
    isChildDiscussion() {
      return !!this.parentDiscussion;
    },
    postedAt() {
      const createdAt = new Date(this.discussion.createdAt);
      return `${format(createdAt, 'MMM d, yyyy')} · ${timeAgo(createdAt)}`;
    },
    replyInputKey() {
      return `discussion-reply-${this.discussion.id}`;
    },
    sanitizedMessage() {
      return sanitizeHtml(this.discussion.message);
    },
    shouldShowFollowUnfollow() {
      let discussionsEnabled = this.$store?.getters['productVars/get']['follow_discussions']?.parsedValue;
      return this.allowFollow && !this.isChildDiscussion && discussionsEnabled;
    },
    showExpandAction() {
      return !this.isChildDiscussion && this.childDiscussionCount > 0;
    },
    showReplyAction() {
      return this.allowReply && !this.isChildDiscussion;
    },
  },
  methods: {
    createDiscussion(params) {
      this.$emit('create-discussion', params);
    },
    editDiscussion(params) {
      this.$emit('edit-discussion', params);
    },
    followUnfollow() {
      if( this.discussion.following ) {
        this.$store.dispatch('discussions/unfollowDiscussion', this.discussion);
        this.$emit('unfollowed-discussion', this.discussion);
      } else {
        this.$store.dispatch('discussions/followDiscussion', this.discussion);
        this.$emit('followed-discussion', this.discussion);
      }
    },
    setEditing(value = true) {
      this.editing = value;
      if (!value) {
        this.$emit('set-input', null);
      }
    },
    setInput(key) {
      this.$emit('set-input', key);
    },
    toggleExpanded() {
      if (!this.expandedReplies) {
        this.$emit('expanded-replies');
      }
      this.expandedReplies = !this.expandedReplies;
    },
    triggerDelete(discussion) {
      this.$emit('trigger-delete', discussion);
    },
  },
};
</script>

<style lang="scss" scoped>
.discussion-component-container {
  margin-top: rem-calc(16);

  textarea {
    margin-top: rem-calc(16);
  }

  > .details {
    align-items: center;
    color: #232323;
    display: flex;
    font-size: rem-calc(16);
    font-weight: 600;

    &.hlt {
      color: #247fdb;
      font-weight: $bold;

      img {
        height: rem-calc(16);
        margin-left: rem-calc(6);
        width: rem-calc(16);
      }
    }
  }

  > .message {
    color: #354052;
    font-size: rem-calc(16);
    margin: rem-calc(8) 0;
    overflow-wrap: break-word;
  }

  > .actions {
    color: #808793;
    font-size: rem-calc(14);
    display: flex;

    button {
      background: none;
      color: #247fdb;
      margin-right: rem-calc(8);
      padding: 0;
      font-weight: 600;

      &.reply-button {
        color: #808793;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    :deep(.discussion-rating-component) {
      margin-left: auto;
    }
  }

  button.delete {
    color: red !important;
  }

  &.child-discussion {
    border-left: 3px solid #CDD0D5;
    margin: rem-calc(16) 0 0 rem-calc(16);
    padding-left: rem-calc(16);

    .details {
      align-items: center;
      color: #232323;
      display: flex;
      font-size: rem-calc(16);
      font-weight: 600;
      margin-bottom: rem-calc(4);

      &.hlt {
        color: #247fdb;
        font-weight: $bold;

        img {
          height: rem-calc(14);
          margin-left: rem-calc(4);
          width: rem-calc(14);
        }
      }
    }

    .message {
      color: #354052;
      font-size: rem-calc(16);
      font-weight: 400;
    }

    .actions {
      margin-top: rem-calc(2);
      display: flex;

      button {
        background: none;
        color: #247fdb;
        font-size: rem-calc(12);
        margin-right: rem-calc(8);
        padding: 0;
      }

      :deep(.discussion-rating-component) {
        margin-left: auto;
      }
    }
  }

  > .posted-at {
    color: #808793;
    display: block;
    font-size: rem-calc(12);
    margin-top: rem-calc(12);
  }

  &.boxed {
    background: #fff;
    border: 1px solid #E3E8EF;
    border-radius: rem-calc(12);
    box-shadow: 0 1px 3px 0 #1018281A;
    margin: rem-calc(12) 0 0;
    padding: rem-calc(19) rem-calc(22);
    @include list-item-fade-in;

    &.featured {
      background-color: #FFF8E1;
      border-color: #F5D27A;
    }

    &.opening-post {
      border-left: 4px solid #326FF0;
      margin-bottom: rem-calc(24);
      padding: rem-calc(24) rem-calc(28);

      > .message {
        font-size: rem-calc(17);
      }
    }

    .label {
      background: #E7F4FE;
      border-radius: 10px;
      color: #247fdb;
      font-size: rem-calc(11);
      font-weight: 600;
      margin-left: rem-calc(8);
      padding: rem-calc(2) rem-calc(8);
      text-transform: uppercase;
    }
  }
}
</style>
